import { useContext, useEffect, useRef, useState, type FC } from "react";
import { Button, Form, Input, Modal, SelectPicker } from "rsuite";
import { Admin } from "../../../models/system/admin";
import { uploadFile } from "../../../services/FileApi";
import { errorToast } from "../../../utils/helper-ui";
import { BsCamera, BsEye, BsEyeSlash } from "react-icons/bs";
import {
  createAdmin,
  getRoles,
  updateAdmin,
} from "../../../services/inventory/SystemApi";
import { Role } from "../../../models/system/role";
import { MdPassword } from "react-icons/md";
import { generatePassword } from "../../../utils/helper";
import { LoadingContext } from "../../../contexts/LoadingContext";

interface AdminFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Admin) => void;
  admin?: Admin;
}

const AdminForm: FC<AdminFormProps> = ({ open, admin, onClose, onSubmit }) => {
  const [data, setData] = useState<Admin>({
    id: "",
    full_name: "",
    username: "",
    email: "",
    roles: [],
  });
  const [uploading, setUploading] = useState(false);
  const [token, setToken] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { isLoading, setIsLoading } = useContext(LoadingContext);

  useEffect(() => {
    if (admin) {
      admin.role_id = admin?.roles?.length > 0 ? admin?.roles[0].id : null;
      setData(admin);
    } else {
      setData({
        id: "",
        full_name: "",
        username: "",
        email: "",
        roles: [],
        profile_picture: null,
      });
    }

    getRoles({ page: 1, size: 10 }, "admin")
      .then((res) => res.json())
      .then((res) => {
        setRoles(res.data.items);
      });
    // if ((admin?.roles ?? []).length > 0) {
    //   setData({
    //     ...data,
    //     role_id: admin!.roles[0].id,
    //   });
    // }
  }, [admin]);

  const renderInputFile = () => (
    <input
      type="file"
      accept="image/*"
      ref={fileRef}
      className="hidden"
      onChange={async (val) => {
        if (val.target.files?.length == 0) return;
        try {
          setUploading(true);
          let resp = await uploadFile(
            val.target.files![0],
            {
              ref_id: admin?.id,
              ref_type: "admin",
            },
            (progress) => {
              console.log(progress);
            }
          );

          console.log(resp);
          let respJson = await resp.json();
          setData({
            ...data,
            profile_picture: respJson.file,
          });
        } catch (error) {
          errorToast(`${error}`);
        } finally {
          setUploading(false);
        }
      }}
    />
  );

  return (
    <Modal
      size={"lg"}
      open={open}
      onClose={() => {
        onClose();
        setData({
          id: "",
          full_name: "",
          username: "",
          email: "",
          roles: [],
          profile_picture: null,
        });
      }}
    >
      <Modal.Header>
        <Modal.Title>Admin Form</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form className="w-full" layout="horizontal" fluid>
          <div className="form-group">
            <label>Name</label>
            <Input
              name="name"
              placeholder="e.g. John Doe"
              value={data.full_name}
              onChange={(value) => setData({ ...data, full_name: value })}
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <Input
              disabled={data.id != ""}
              name="email"
              placeholder="e.g. Hq5bP@example.com"
              value={data.email}
              onChange={(value) => setData({ ...data, email: value })}
            />
          </div>
          <div className="form-group">
            <label>Role</label>
            <div className="input">
              <SelectPicker
                name="role_id"
                data={roles}
                labelKey="name"
                valueKey="id"
                block
                value={data.role_id}
                onChange={(value) => setData({ ...data, role_id: value! })}
              />
            </div>
          </div>
          {data.id != "" && (
            <div className="form-group">
              <label>Phone</label>
              <Input
                name="phone"
                placeholder="e.g. 08123456789"
                value={data.phone}
                onChange={(value) => setData({ ...data, phone: value })}
              />
            </div>
          )}
          {data.id == "" && (
            <div className="form-group">
              <label>Password</label>
              <div className="input ">
                <div className="absolute w-full ">
                  <Input
                    name="password"
                    placeholder="* * * * * * * * *"
                    value={password}
                    type={showPassword ? "text" : "password"}
                    onChange={(value) => setPassword(value!)}
                  />
                  <button
                    type="button"
                    className="absolute right-8 top-2"
                    onClick={() => {
                      let newPassword = generatePassword();
                      setPassword(newPassword);
                    }}
                  >
                    <MdPassword />
                  </button>
                  <button
                    type="button"
                    className="absolute right-2 top-2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <BsEyeSlash size={16} />
                    ) : (
                      <BsEye size={16} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {data.id != "" && (
            <div className="form-group">
              <label>Profile Picture</label>
              <div className="input">
                <Button
                  loading={uploading}
                  className="flex justify-center items-center "
                  onClick={() => fileRef.current?.click()}
                >
                  {data.profile_picture ? (
                    <img
                      src={data.profile_picture.url}
                      alt=""
                      className="aspect-square w-32 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="p-8">
                      <BsCamera size={48} />
                    </div>
                  )}
                </Button>
              </div>
            </div>
          )}
        </Form>
        <Modal.Footer>
          <div className="pt-4">
            <Button
              appearance="primary"
              loading={isLoading}
              onClick={() => {
                if (!data.role_id) {
                  errorToast("Please select role");
                  return;
                }
                setIsLoading(true);
                if (data.id == "") {
                  createAdmin(
                    data.full_name,
                    data.email,
                    password,
                    data?.role_id!
                  )
                    .then((res) => {
                      onSubmit(data);
                    })
                    .catch(errorToast)
                    .finally(() => setIsLoading(false));
                } else {
                  updateAdmin(data.id, data)
                    .then((res) => {
                      onSubmit(data);
                    })
                    .catch(errorToast)
                    .finally(() => setIsLoading(false));
                }
              }}
            >
              Submit
            </Button>
            <Button appearance="link" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </Modal.Footer>
      </Modal.Body>
      {renderInputFile()}
    </Modal>
  );
};
export default AdminForm;
